import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { PaymentForm, CreditCard } from "react-square-web-payments-sdk";
import { useAlert } from "react-alert";
import api from "../../api";

const font = "just-sans, sans-serif";

export const SquareCardModal = ({ show, handleClose, email }) => {
  const alert = useAlert();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [formReady, setFormReady] = useState(false);
  const [iframesReady, setIframesReady] = useState(false);
  const [activating, setActivating] = useState(false);

  useEffect(() => {
    let t1, t2;
    if (show) {
      setError("");
      setLoading(false);
      t1 = setTimeout(() => setFormReady(true), 600);
      t2 = setTimeout(() => setIframesReady(true), 3200);
    } else {
      setFormReady(false);
      setIframesReady(false);
    }
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [show]);

  const handleTokenize = async (token) => {
    if (!token?.token) {
      setError("Card tokenization failed. Please check your card details.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await api.createSquareSubscription({ email, sourceId: token.token });
      if (res?.success) {
        alert.success("Payment successful! Activating your subscription...");
        setActivating(true);
        setTimeout(() => { window.location.href = "/dashboard"; }, 10000);
      } else {
        setError("Subscription creation failed. Please try again.");
      }
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Payment failed. Please try again.");
    }
    setLoading(false);
  };

  return (
    <Modal show={show} onHide={activating ? () => {} : handleClose} centered>
      <Modal.Body style={{ padding: 0, borderRadius: "14px", overflow: "hidden", fontFamily: font }}>

        {/* Activating screen */}
        {activating && (
          <div style={{ textAlign: "center", padding: "48px 32px 40px", fontFamily: font }}>
            <i className="fa fa-spinner fa-pulse" style={{ fontSize: "38px", color: "#7c3aed", marginBottom: "20px", display: "block" }} />
            <h5 style={{ fontWeight: 800, fontSize: "20px", color: "#1a1a2e", marginBottom: "10px", fontFamily: font }}>
              Activating Your Subscription
            </h5>
            <p style={{ fontSize: "14px", color: "#777", margin: 0, lineHeight: 1.6 }}>
              Please wait while we confirm your payment.<br />
              You'll be redirected to your dashboard shortly.
            </p>
          </div>
        )}

        {!activating && (
          <div>
            {/* Header bar */}
            <div style={{
              background: "linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)",
              padding: "22px 24px 20px",
              position: "relative",
            }}>
              <h5 style={{
                margin: 0,
                fontWeight: 700,
                fontSize: "20px",
                color: "#fff",
                fontFamily: font,
                letterSpacing: "-0.3px",
              }}>
                Complete Payment
              </h5>
              <p style={{ margin: "4px 0 0", fontSize: "13px", color: "rgba(255,255,255,0.75)", fontFamily: font }}>
                Secure checkout powered by Square
              </p>
              <button
                onClick={handleClose}
                disabled={loading}
                style={{
                  position: "absolute", top: "16px", right: "16px",
                  background: "rgba(255,255,255,0.15)", border: "none",
                  borderRadius: "50%", width: "28px", height: "28px",
                  fontSize: "16px", cursor: "pointer", color: "#fff",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  lineHeight: 1, padding: 0,
                }}
              >
                &times;
              </button>
            </div>

            <div style={{ padding: "24px 24px 20px" }}>
              {/* Plan summary */}
              <div style={{
                background: "#f5f3ff",
                border: "1.5px solid #ede9fe",
                borderRadius: "12px",
                padding: "16px 18px",
                marginBottom: "22px",
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                  <span style={{
                    fontWeight: 700,
                    fontSize: "16px",
                    color: "#1a1a2e",
                    fontFamily: font,
                    letterSpacing: "-0.2px",
                  }}>
                    Monthly Subscription
                  </span>
                  <span style={{
                    fontWeight: 800,
                    fontSize: "20px",
                    color: "#7c3aed",
                    fontFamily: font,
                    letterSpacing: "-0.5px",
                  }}>
                    $10<span style={{ fontSize: "13px", fontWeight: 600, color: "#9d7eef" }}> / mo</span>
                  </span>
                </div>
                <div style={{ borderTop: "1px solid #e5e0f9", paddingTop: "8px" }}>
                  <span style={{ fontSize: "12.5px", color: "#888", fontFamily: "'Jost', sans-serif" }}>
                    Billed to&nbsp;
                    <strong style={{ color: "#5b21b6", fontWeight: 700 }}>{email}</strong>
                  </span>
                </div>
              </div>

              {/* Card form */}
              <div style={{ position: "relative", marginBottom: "4px" }}>
                <p style={{
                  fontWeight: 700,
                  fontSize: "13px",
                  color: "#444",
                  marginBottom: "10px",
                  fontFamily: font,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}>
                  Card Details
                </p>

                {!iframesReady && (
                  <div style={{
                    position: formReady ? "absolute" : "relative",
                    inset: 0,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "#fff",
                    zIndex: 2,
                    padding: formReady ? 0 : "28px 0",
                    minHeight: formReady ? "130px" : undefined,
                  }}>
                    <i className="fa fa-spinner fa-pulse" style={{ fontSize: "22px", color: "#7c3aed", marginBottom: "10px" }} />
                    <span style={{ fontSize: "13px", color: "#999", fontFamily: font }}>Loading secure payment form...</span>
                  </div>
                )}

                {formReady && (
                  <div style={{ visibility: iframesReady ? "visible" : "hidden" }}>
                    <PaymentForm
                      applicationId={process.env.REACT_APP_SQUARE_APP_ID}
                      locationId={process.env.REACT_APP_SQUARE_LOCATION_ID}
                      cardTokenizeResponseReceived={handleTokenize}
                    >
                      <CreditCard
                        buttonProps={{
                          isLoading: loading,
                          css: {
                            backgroundColor: "#7c3aed",
                            color: "#fff",
                            fontSize: "15px",
                            fontWeight: "700",
                            borderRadius: "10px",
                            padding: "13px",
                            width: "100%",
                            marginTop: "10px",
                            fontFamily: font,
                            letterSpacing: "-0.2px",
                          },
                        }}
                      >
                        Pay $10 / month
                      </CreditCard>
                    </PaymentForm>
                  </div>
                )}
              </div>

              {error && (
                <p style={{ color: "#dc2626", marginTop: "10px", fontSize: "13px", textAlign: "center", fontFamily: font }}>
                  {error}
                </p>
              )}

              {/* Cancel */}
              <div style={{ textAlign: "center", marginTop: "14px" }}>
                <button
                  onClick={handleClose}
                  disabled={loading}
                  style={{
                    background: "none", border: "none",
                    color: "#aaa", fontSize: "13px",
                    cursor: "pointer", textDecoration: "underline",
                    fontFamily: font,
                  }}
                >
                  Cancel
                </button>
              </div>

              {/* Trust badges */}
              <div style={{
                display: "flex", justifyContent: "center",
                gap: "20px", marginTop: "14px",
                paddingTop: "14px", borderTop: "1px solid #f0f0f0",
              }}>
                <span style={{ fontSize: "11px", color: "#bbb", fontFamily: font }}>🔒 Secure encrypted checkout</span>
                <span style={{ fontSize: "11px", color: "#bbb", fontFamily: font }}>✓ Cancel anytime</span>
              </div>
            </div>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};
